import React, { useState } from 'react';
import { MODULES, MAPPED_CONTACTS } from '../../constants';
import { useApp } from '../../context/AppContext';
import { useBreakpoint } from '../../hooks/useBreakpoint';

type TeamTab = 'teams' | 'members' | 'modules';
type Team = { name: string; members: number; playbooks: number; leads: number; color: string; manager: string };

const TEAM_COLORS = ['#3B82F6', '#8B5CF6', '#22C55E', '#EF4444', '#C8973A', '#06B6D4', '#EC4899'];
const MANAGERS = ['Rahul Nair', 'Sneha Kapoor', 'Arjun Mehta'];
const INIT_TEAMS: Team[] = [
  { name: 'North India Sales', members: 4, playbooks: 12, leads: 28, color: '#3B82F6', manager: 'Rahul Nair' },
  { name: 'South India Sales', members: 3, playbooks: 8,  leads: 14, color: '#8B5CF6', manager: 'Sneha Kapoor' },
  { name: 'Enterprise Team',   members: 2, playbooks: 5,  leads: 9,  color: '#22C55E', manager: 'Arjun Mehta' },
];

/* ── shared primitives ── */
const OVERLAY: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(15,27,45,.6)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' };
const CARD: React.CSSProperties    = { background: 'white', borderRadius: '18px', width: '100%', maxWidth: '480px', boxShadow: '0 32px 80px rgba(0,0,0,.3)', overflow: 'hidden' };
const HDR: React.CSSProperties     = { background: '#0F1B2D', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: '12px' };
const BODY: React.CSSProperties    = { padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' };
const LBL: React.CSSProperties     = { display: 'block', fontSize: '13px', fontWeight: '600', color: '#1A202C', marginBottom: '6px' };
const fieldInp = (err: boolean): React.CSSProperties => ({
  width: '100%', padding: '11px 14px', borderRadius: '10px', fontFamily: 'inherit', fontSize: '14px',
  border: `1.5px solid ${err ? '#FC8181' : '#E2E8F0'}`, outline: 'none', boxSizing: 'border-box',
  background: err ? '#FFF5F5' : 'white', color: '#1A202C',
});

/* ── TeamModal ── */
function TeamModal({ initial, onSave, onClose }: { initial?: Team; onSave: (t: Team) => void; onClose: () => void }) {
  const [form, setForm] = useState<Team>(initial ?? { name: '', members: 0, playbooks: 0, leads: 0, color: '#3B82F6', manager: MANAGERS[0] });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = <K extends keyof Team>(k: K, v: Team[K]) => {
    setForm(prev => ({ ...prev, [k]: v }));
    setErrors(prev => ({ ...prev, [k]: '' }));
  };

  const submit = () => {
    if (!form.name.trim()) { setErrors({ name: 'Team name is required' }); return; }
    onSave({ ...form, name: form.name.trim() });
    onClose();
  };

  return (
    <div style={OVERLAY} onClick={onClose}>
      <div style={CARD} onClick={e => e.stopPropagation()}>
        <div style={HDR}>
          <i className="ti ti-users" style={{ fontSize: '20px', color: '#C8973A' }} />
          <span style={{ fontSize: '17px', fontWeight: '700', color: 'white', flex: 1 }}>{initial ? 'Edit team' : 'New team'}</span>
          <button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,.15)', border: 'none', borderRadius: '8px', color: 'white', width: '30px', height: '30px', cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>
        <div style={BODY}>
          <div>
            <label style={LBL}>Team name *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. West India Sales" style={fieldInp(!!errors.name)} />
            {errors.name && <div style={{ fontSize: '11px', color: '#E53E3E', marginTop: '4px' }}>{errors.name}</div>}
          </div>
          <div>
            <label htmlFor="team-mgr" style={LBL}>Manager</label>
            <select id="team-mgr" value={form.manager} onChange={e => set('manager', e.target.value)}
              style={{ width: '100%', padding: '11px 14px', borderRadius: '10px', border: '1.5px solid #E2E8F0', fontFamily: 'inherit', fontSize: '14px', background: 'white', outline: 'none', cursor: 'pointer', color: '#1A202C' }}>
              {MANAGERS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label style={LBL}>Team colour</label>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {TEAM_COLORS.map(c => (
                <button key={c} type="button" onClick={() => set('color', c)}
                  style={{ width: '32px', height: '32px', borderRadius: '50%', background: c, border: form.color === c ? '3px solid #0F1B2D' : '3px solid transparent', cursor: 'pointer', outline: form.color === c ? '2px solid white' : 'none', outlineOffset: '-5px', transition: 'transform .1s' }} />
              ))}
            </div>
          </div>
        </div>
        <div style={{ padding: '0 24px 24px' }}>
          <button type="button" onClick={submit}
            style={{ width: '100%', padding: '14px', borderRadius: '12px', background: '#0F1B2D', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '15px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <i className={initial ? 'ti ti-check' : 'ti ti-plus'} /> {initial ? 'Save changes' : 'Create team'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── ConfirmModal ── */
function ConfirmModal({ message, onConfirm, onClose }: { message: string; onConfirm: () => void; onClose: () => void }) {
  return (
    <div style={OVERLAY} onClick={onClose}>
      <div style={{ ...CARD, maxWidth: '380px' }} onClick={e => e.stopPropagation()}>
        <div style={HDR}>
          <i className="ti ti-alert-triangle" style={{ fontSize: '20px', color: '#EF4444' }} />
          <span style={{ fontSize: '17px', fontWeight: '700', color: 'white', flex: 1 }}>Confirm action</span>
          <button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,.15)', border: 'none', borderRadius: '8px', color: 'white', width: '30px', height: '30px', cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>
        <div style={{ padding: '24px' }}>
          <p style={{ fontSize: '14px', color: '#4A5568', lineHeight: 1.6, marginBottom: '20px' }}>{message}</p>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1.5px solid #E2E8F0', background: 'white', fontFamily: 'inherit', fontSize: '14px', cursor: 'pointer', color: '#4A5568', fontWeight: '600' }}>Cancel</button>
            <button type="button" onClick={() => { onConfirm(); onClose(); }} style={{ flex: 1, padding: '12px', borderRadius: '10px', background: '#EF4444', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>Remove</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TeamScreen() {
  const { toast } = useApp();
  const { isMobile, rp } = useBreakpoint();
  const [tab, setTab] = useState<TeamTab>('teams');
  const [teams, setTeams] = useState<Team[]>(INIT_TEAMS);
  const [showNewTeam, setShowNewTeam] = useState(false);
  const [editTeam, setEditTeam] = useState<{ idx: number; team: Team } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<{ name: string; idx: number } | null>(null);
  const [members, setMembers] = useState(MAPPED_CONTACTS);
  const modulesList = Object.values(MODULES);
  const coreModules = modulesList.filter(m => m.core);
  const optionalModules = modulesList.filter(m => !m.core);
  const [moduleStates, setModuleStates] = useState<Record<string, boolean>>(
    Object.fromEntries(modulesList.map(m => [m.id, m.enabled]))
  );
  const px = rp({ mobile: '14px', tablet: '18px', desktop: '24px' });

  const toggleModule = (id: string) => {
    const m = MODULES[id];
    if (m.core) { toast('Core modules cannot be disabled'); return; }
    setModuleStates(prev => ({ ...prev, [id]: !prev[id] }));
    toast(`${m.label} ${moduleStates[id] ? 'disabled' : 'enabled'}`);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: `14px ${px}`, borderBottom: '1px solid var(--border)', flexShrink: 0, background: 'white' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#E0E7FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className="ti ti-users" style={{ fontSize: '18px', color: '#4F46E5' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: rp({ mobile: '16px', desktop: '20px' }), fontWeight: '800', color: 'var(--navy)' }}>Team & Modules</div>
          {!isMobile && <div style={{ fontSize: '12px', color: 'var(--text3)' }}>Manage teams, members, and module visibility</div>}
        </div>
        {tab === 'teams' && (
          <button type="button" onClick={() => setShowNewTeam(true)} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: isMobile ? '7px 10px' : '8px 16px', borderRadius: '9px', background: '#C8973A', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '12px', fontWeight: '700', cursor: 'pointer', flexShrink: 0 }}>
            <i className="ti ti-plus" />{!isMobile && ' New Team'}
          </button>
        )}
        {tab === 'members' && (
          <button type="button" onClick={() => toast('Invite sent to new member', 'success')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: isMobile ? '7px 10px' : '8px 16px', borderRadius: '9px', background: '#4F46E5', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '12px', fontWeight: '700', cursor: 'pointer', flexShrink: 0 }}>
            <i className="ti ti-user-plus" />{!isMobile && ' Invite'}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', padding: `8px ${px}`, background: 'var(--surface)', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        {([
          { id: 'teams',   icon: 'ti-building',  label: 'Teams'   },
          { id: 'members', icon: 'ti-user-check', label: 'Members' },
          { id: 'modules', icon: 'ti-plug',       label: 'Modules' },
        ] as const).map(t => (
          <button type="button" key={t.id} onClick={() => setTab(t.id)} style={{ padding: '7px 14px', borderRadius: '8px', border: 'none', background: tab === t.id ? 'white' : 'none', fontFamily: 'inherit', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', color: tab === t.id ? 'var(--navy)' : 'var(--text2)', boxShadow: tab === t.id ? '0 1px 4px rgba(0,0,0,.08)' : 'none' }}>
            <i className={`ti ${t.icon}`} /> {t.label}
          </button>
        ))}
      </div>

      {/* TEAMS */}
      {tab === 'teams' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: `16px ${px}` }}>
          <div style={{ display: 'grid', gridTemplateColumns: rp({ mobile: '1fr', tablet: '1fr 1fr', desktop: 'repeat(3,1fr)' }), gap: '14px' }}>
            {teams.map((team, i) => (
              <div key={i} style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '14px', overflow: 'hidden' }}>
                <div style={{ height: '5px', background: team.color }} />
                <div style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--navy)', marginBottom: '2px' }}>{team.name}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text3)' }}>Manager: {team.manager}</div>
                    </div>
                    <button type="button" onClick={() => setEditTeam({ idx: i, team })} style={{ padding: '4px 9px', borderRadius: '6px', border: '1.5px solid var(--border2)', background: 'white', fontFamily: 'inherit', fontSize: '11px', cursor: 'pointer', color: 'var(--text2)' }}>Edit</button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '6px', marginBottom: '12px' }}>
                    {[{ val: team.members, label: 'Members' }, { val: team.playbooks, label: 'Playbooks' }, { val: team.leads, label: 'Leads' }].map(s => (
                      <div key={s.label} style={{ textAlign: 'center', padding: '8px', background: 'var(--surface)', borderRadius: '8px' }}>
                        <div style={{ fontSize: '18px', fontWeight: '800', color: 'var(--navy)' }}>{s.val}</div>
                        <div style={{ fontSize: '9px', color: 'var(--text3)', fontWeight: '600', textTransform: 'uppercase' }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button type="button" onClick={() => setTab('members')} style={{ flex: 1, padding: '7px', borderRadius: '7px', border: '1.5px solid var(--border2)', background: 'white', fontFamily: 'inherit', fontSize: '11px', color: 'var(--text2)', cursor: 'pointer' }}>Members</button>
                    <button type="button" onClick={() => setTab('modules')} style={{ flex: 1, padding: '7px', borderRadius: '7px', background: 'var(--navy)', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}>Modules</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MEMBERS */}
      {tab === 'members' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: `16px ${px}` }}>
          {isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {members.map((c, i) => (
                <div key={i} style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '12px', padding: '12px 14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                    <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: c.segment === 'client' ? 'var(--navy)' : c.segment === 'agent' ? '#6366F1' : '#22C55E', color: 'white', fontSize: '11px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{c.ini}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--navy)' }}>{c.name}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text3)' }}>{c.role} · {c.org}</div>
                    </div>
                    <span style={{ fontSize: '9px', fontWeight: '700', padding: '2px 8px', borderRadius: '20px', background: c.segment === 'client' ? '#EBF8FF' : c.segment === 'agent' ? '#EEF2FF' : '#F0FFF4', color: c.segment === 'client' ? '#1E4E8C' : c.segment === 'agent' ? '#4F46E5' : '#276749', textTransform: 'capitalize' }}>{c.segment}</span>
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text3)', marginBottom: '8px' }}>{c.email}</div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <a href={`mailto:${c.email}`} style={{ flex: 1, padding: '6px', borderRadius: '7px', border: '1px solid var(--border2)', background: 'white', fontFamily: 'inherit', fontSize: '11px', color: 'var(--text2)', cursor: 'pointer', textAlign: 'center', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                      <i className="ti ti-mail" style={{ fontSize: '12px' }} /> Email
                    </a>
                    <button type="button" onClick={() => setConfirmRemove({ name: c.name, idx: i })} style={{ padding: '6px 12px', borderRadius: '7px', border: '1px solid #FECDD3', background: '#FFF5F5', fontFamily: 'inherit', fontSize: '11px', cursor: 'pointer', color: '#C53030' }}>Remove</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
                    {['Name', 'Role', 'Organization', 'Email', 'Segment', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {members.map((c, i) => (
                    <tr key={i} style={{ borderBottom: i < members.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <td style={{ padding: '11px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: c.segment === 'client' ? 'var(--navy)' : c.segment === 'agent' ? '#6366F1' : '#22C55E', color: 'white', fontSize: '9px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{c.ini}</div>
                          <span style={{ fontWeight: '600', color: 'var(--navy)' }}>{c.name}</span>
                        </div>
                      </td>
                      <td style={{ padding: '11px 12px', color: 'var(--text2)' }}>{c.role}</td>
                      <td style={{ padding: '11px 12px', color: 'var(--text3)' }}>{c.org}</td>
                      <td style={{ padding: '11px 12px' }}>
                        <a href={`mailto:${c.email}`} style={{ color: '#3B82F6', fontSize: '11px', textDecoration: 'none' }}>{c.email}</a>
                      </td>
                      <td style={{ padding: '11px 12px' }}>
                        <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '20px', background: c.segment === 'client' ? '#EBF8FF' : c.segment === 'agent' ? '#EEF2FF' : '#F0FFF4', color: c.segment === 'client' ? '#1E4E8C' : c.segment === 'agent' ? '#4F46E5' : '#276749', textTransform: 'capitalize' }}>{c.segment}</span>
                      </td>
                      <td style={{ padding: '11px 12px' }}>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <a href={`mailto:${c.email}`} style={{ padding: '4px 8px', borderRadius: '5px', border: '1px solid var(--border2)', background: 'white', fontFamily: 'inherit', fontSize: '10px', cursor: 'pointer', color: 'var(--text2)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                            <i className="ti ti-mail" /> Email
                          </a>
                          <button type="button" onClick={() => setConfirmRemove({ name: c.name, idx: i })} style={{ padding: '4px 8px', borderRadius: '5px', border: '1px solid #FECDD3', background: '#FFF5F5', fontFamily: 'inherit', fontSize: '10px', cursor: 'pointer', color: '#C53030' }}>Remove</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* MODULES */}
      {tab === 'modules' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: `16px ${px}` }}>
          <div style={{ maxWidth: '900px' }}>
            <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: '8px', padding: '10px 14px', marginBottom: '18px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <i className="ti ti-info-circle" style={{ fontSize: '15px', color: '#B45309', flexShrink: 0, marginTop: '2px' }} />
              <div style={{ fontSize: '12px', color: '#92400E', lineHeight: 1.5 }}>Core modules are always enabled. Toggle optional modules to control access for your team.</div>
            </div>

            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '10px' }}>Core Modules</div>
            <div style={{ display: 'grid', gridTemplateColumns: rp({ mobile: '1fr', tablet: '1fr 1fr', desktop: '1fr 1fr' }), gap: '8px', marginBottom: '20px' }}>
              {coreModules.map(m => (
                <div key={m.id} style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '10px', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '10px', opacity: .65 }}>
                  <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: m.color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <i className={`ti ${m.icon}`} style={{ color: m.color, fontSize: '15px' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--navy)' }}>{m.label}</div>
                    {!isMobile && <div style={{ fontSize: '10px', color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.desc}</div>}
                  </div>
                  <span style={{ fontSize: '9px', fontWeight: '700', padding: '2px 8px', borderRadius: '20px', background: '#F0FFF4', color: '#276749', border: '1px solid #9AE6B4', flexShrink: 0 }}>Core</span>
                </div>
              ))}
            </div>

            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '10px' }}>Optional Modules</div>
            <div style={{ display: 'grid', gridTemplateColumns: rp({ mobile: '1fr', tablet: '1fr 1fr', desktop: '1fr 1fr' }), gap: '8px' }}>
              {optionalModules.map(m => {
                const on = moduleStates[m.id];
                return (
                  <div key={m.id} style={{ background: 'white', border: `1.5px solid ${on ? m.color + '40' : 'var(--border)'}`, borderRadius: '10px', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: on ? m.color + '20' : 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className={`ti ${m.icon}`} style={{ color: on ? m.color : 'var(--text3)', fontSize: '15px' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '12px', fontWeight: '700', color: on ? 'var(--navy)' : 'var(--text3)' }}>{m.label}</div>
                      {!isMobile && <div style={{ fontSize: '10px', color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.desc}</div>}
                    </div>
                    <button type="button" aria-label={`${on ? 'Disable' : 'Enable'} ${m.label}`} onClick={() => toggleModule(m.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0 }}>
                      <div style={{ width: '34px', height: '19px', borderRadius: '10px', background: on ? m.color : '#CBD5E0', position: 'relative', transition: 'background .2s' }}>
                        <div style={{ position: 'absolute', top: '2px', left: on ? '17px' : '2px', width: '15px', height: '15px', borderRadius: '50%', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,.2)', transition: 'left .2s' }} />
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {showNewTeam && (
        <TeamModal
          onSave={t => { setTeams(prev => [...prev, { ...t, members: 0, playbooks: 0, leads: 0 }]); toast(`Team "${t.name}" created`, 'success'); }}
          onClose={() => setShowNewTeam(false)}
        />
      )}
      {editTeam && (
        <TeamModal
          initial={editTeam.team}
          onSave={t => { setTeams(prev => prev.map((x, i) => i === editTeam.idx ? { ...x, ...t } : x)); toast(`Team "${t.name}" updated`, 'success'); }}
          onClose={() => setEditTeam(null)}
        />
      )}
      {confirmRemove && (
        <ConfirmModal
          message={`Remove ${confirmRemove.name} from the team? This cannot be undone.`}
          onConfirm={() => { setMembers(prev => prev.filter((_, i) => i !== confirmRemove.idx)); toast(`${confirmRemove.name} removed`, 'success'); }}
          onClose={() => setConfirmRemove(null)}
        />
      )}
    </div>
  );
}
